import type ArrayProxy from '@ember/array/proxy';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { endOfDay, startOfDay } from 'date-fns';
import { format, formatInTimeZone } from 'date-fns-tz';
import type AbilitiesService from 'ember-can/services/abilities';
import { keepLatestTask, task } from 'ember-concurrency';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type AnnouncementModel from 'garaje/models/announcement';
import type TakeoverModel from 'garaje/models/takeover';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { DATE_FNS_YYYY_MM_DD, modifyDateInTimeZone, parseYyyyMmDdInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { reads } from 'macro-decorators';

import { getAnnouncementQueryParams } from './announcement-queries';
import { fetchIncidentsByAnnouncementId, type IncidentSummary } from './incident-queries';
import { PAGE_SIZE } from './route-base';
import type { AnnoucementsListModel } from './route-base';

const DEFAULT_TIMEZONE = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/Los_Angeles';

export enum MessageLogColumn {
  Incident = 'Incident',
  ActivePeriod = 'Active period',
  CreatedBy = 'Created by',
  Recipients = 'Recipients',
  SentAt = 'Sent at',
  Message = 'Message',
  Location = 'Location',
}

export interface MessageLogEntry {
  id: string;
  sentAt: string;
  sender: string;
  senderId: string;
  senderProfilePictureUrl?: string;
  messageTitle: string;
  messageBody: string;
  location?: string;
  category?: string;
  employeeCount: number;
  visitorCount: number;
  markedAsSafeCount: number;
  incidentStatus: 'active' | 'ended' | null;
  activePeriod: string | null;
}

export default class CommunicationsMessageLogListControllerBase extends Controller {
  declare model: AnnoucementsListModel;
  @service declare store: Store;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare currentLocation: CurrentLocationService;
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  @tracked selectedLocationIds: string[] = [];
  @tracked incidentsByAnnouncementId: Map<string, IncidentSummary> = new Map();

  @tracked takeovers: ArrayProxy<TakeoverModel>[] = [];
  @tracked messageLogEntries: MessageLogEntry[] = [];
  @tracked totalCount = 0;
  @tracked currentPage = 1;
  @tracked pageSize = PAGE_SIZE;

  @tracked startDate: Date = new Date();
  @tracked endDate: Date = new Date();
  @tracked showCalendar = false;
  @tracked isActiveInLastSixMonths = false;

  @reads('currentLocation.timezone', DEFAULT_TIMEZONE) timezone!: string;

  initializeData = task({ drop: true }, async (startDate: Date, endDate: Date, fetchCurrentLocation: boolean) => {
    if (!this.model.announcements) {
      throw new Error('No announcements found');
    }

    this.incidentsByAnnouncementId = this.model.incidentsByAnnouncementId ?? new Map();
    this.startDate = startDate;
    this.endDate = endDate;
    const loadedIds = new Set(this.model.announcements.map((a) => a.id));
    const extraAnnouncements = await this.fetchMissingAnnouncements(loadedIds, startDate, endDate);
    this.messageLogEntries = await Promise.all(
      [...(this.model.announcements as unknown as AnnouncementModel[]), ...extraAnnouncements].map((announcement) =>
        this.createMessageLogEntry(announcement),
      ),
    );
    this.selectedLocationIds = fetchCurrentLocation ? [this.currentLocation.location.id] : [];
    this.currentPage = 1;
    this.totalCount = this.model.announcements?.meta?.total || 0;
    this.isActiveInLastSixMonths = this.totalCount > 0;

    this.takeovers =
      (await Promise.all(this.model.announcements.map(async (announcement) => await announcement.takeovers))).flat() ||
      [];
  });

  refreshData = task({ drop: true }, async () => {
    const locationId = this.currentLocation.location.id;
    const queryParams = getAnnouncementQueryParams(this.selectedLocationIds, this.startDate, this.endDate);
    const [announcements, incidentsByAnnouncementId] = await Promise.all([
      this.store.query('announcement', queryParams) as unknown as Promise<PaginatedRecordArray<AnnouncementModel>>,
      this.featureFlags.isEnabled('emno-end-incident-web')
        ? fetchIncidentsByAnnouncementId(locationId, this.startDate, this.endDate)
        : Promise.resolve(new Map<string, IncidentSummary>()),
    ]);
    this.incidentsByAnnouncementId = incidentsByAnnouncementId;
    const loadedAnnouncements = announcements as unknown as AnnouncementModel[];
    const loadedIds = new Set(loadedAnnouncements.map((a) => a.id));
    // extraAnnouncements have createdAt before the range, so sort by incidentStart to place them correctly
    const extraAnnouncements = await this.fetchMissingAnnouncements(loadedIds, this.startDate, this.endDate);
    const sortedAnnouncements = [...loadedAnnouncements, ...extraAnnouncements].sort((a, b) => {
      // eslint-disable-next-line ember/use-ember-get-and-set
      const aIncident = this.incidentsByAnnouncementId.get(a.id);
      // eslint-disable-next-line ember/use-ember-get-and-set
      const bIncident = this.incidentsByAnnouncementId.get(b.id);
      const aTime = aIncident ? new Date(aIncident.startedAt).getTime() : a.createdAt.getTime();
      const bTime = bIncident ? new Date(bIncident.startedAt).getTime() : b.createdAt.getTime();
      return bTime - aTime;
    });
    this.messageLogEntries = await Promise.all(
      sortedAnnouncements.map((announcement) => this.createMessageLogEntry(announcement)),
    );
    this.totalCount = this.messageLogEntries.length;
  });

  get messageLogHeaders(): readonly MessageLogColumn[] {
    if (this.featureFlags.isEnabled('emno-end-incident-web')) {
      return [
        MessageLogColumn.Incident,
        MessageLogColumn.ActivePeriod,
        MessageLogColumn.CreatedBy,
        MessageLogColumn.Recipients,
      ];
    }
    return [MessageLogColumn.SentAt, MessageLogColumn.Message, MessageLogColumn.Recipients];
  }

  get hasMorePages(): boolean {
    return this.pageSize * this.currentPage < this.totalCount;
  }

  @action
  setStartDate(date: Date): void {
    this.startDate = date;
  }

  @action
  setEndDate(date: Date): void {
    this.endDate = date;
  }

  get startDateFormat(): string {
    const { timezone, startDateWithDefault } = this;

    return formatInTimeZone(startDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get endDateFormat(): string {
    const { timezone, endDateWithDefault } = this;

    return formatInTimeZone(endDateWithDefault, timezone, 'MM/dd/yyyy');
  }

  get dateWithDefault(): string {
    return formatInTimeZone(new Date(), this.timezone, DATE_FNS_YYYY_MM_DD);
  }

  get startDateWithDefault(): Date {
    if (this.startDate) return new Date(this.startDate);

    const { dateWithDefault } = this;

    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const start = modifyDateInTimeZone(date, this.timezone, startOfDay);
    return start;
  }

  get endDateWithDefault(): Date {
    if (this.endDate) return new Date(this.endDate);

    const { dateWithDefault } = this;
    const date = parseYyyyMmDdInTimeZone(dateWithDefault, new Date(), this.timezone);
    const end = modifyDateInTimeZone(date, this.timezone, endOfDay);

    return end;
  }

  loadMoreData = keepLatestTask(async () => {
    if (!this.hasMorePages) {
      return;
    }
    const offset = this.currentPage * this.pageSize;
    this.currentPage++;
    const queryParams = getAnnouncementQueryParams(
      this.selectedLocationIds,
      this.startDate,
      this.endDate,
      this.pageSize,
      offset,
    );
    const newAnnouncements = await this.store.query('announcement', queryParams);

    if (Number(newAnnouncements.length) > 0) {
      const newMessagLogEntries = await Promise.all(
        newAnnouncements.map((announcement) => this.createMessageLogEntry(announcement)),
      );
      this.messageLogEntries = [...this.messageLogEntries, ...newMessagLogEntries];
    }
  });

  @action
  async onDatesChanged(startDate: Date, endDate: Date): Promise<void> {
    this.startDate = startDate;
    this.endDate = endDate;
    await this.refreshData.perform();
  }

  get totalMessages(): number {
    return this.messageLogEntries?.length ?? 0;
  }

  locationName = (locationId: string): string => {
    return this.store.peekRecord('skinny-location', locationId)?.name ?? '';
  };

  formatActivePeriod(startDate: Date, endDate: Date | null, timezone?: string): string {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (!endDate) {
      // Active: "Feb 23, 2026 2:42 PM"
      return formatInTimeZone(startDate, tz, 'MMM d, yyyy h:mm a');
    }

    const startYear = parseInt(formatInTimeZone(startDate, tz, 'yyyy'), 10);
    const endYear = parseInt(formatInTimeZone(endDate, tz, 'yyyy'), 10);
    const startDay = formatInTimeZone(startDate, tz, 'yyyy-MM-dd');
    const endDay = formatInTimeZone(endDate, tz, 'yyyy-MM-dd');
    const tzAbbr = formatInTimeZone(endDate, tz, 'zzz');

    if (startDay === endDay) {
      // Same day: "Feb 23, 2026 2:42 PM – 8:30 PM PST"
      const startStr = formatInTimeZone(startDate, tz, 'MMM d, yyyy h:mm a');
      const endStr = formatInTimeZone(endDate, tz, 'h:mm a');
      return `${startStr} – ${endStr} ${tzAbbr}`;
    }

    if (startYear === endYear) {
      // Multi-day same year: "Feb 23, 2:42 PM – Feb 24, 8:30 PM PST"
      const startStr = formatInTimeZone(startDate, tz, 'MMM d, h:mm a');
      const endStr = formatInTimeZone(endDate, tz, 'MMM d, h:mm a');
      return `${startStr} – ${endStr} ${tzAbbr}`;
    }

    // Multi-year: "Dec 31, 2025, 11:00 PM – Jan 1, 2026, 2:00 AM PST"
    const startStr = formatInTimeZone(startDate, tz, 'MMM d, yyyy, h:mm a');
    const endStr = formatInTimeZone(endDate, tz, 'MMM d, yyyy, h:mm a');
    return `${startStr} – ${endStr} ${tzAbbr}`;
  }

  formatDate(date: Date, timezone?: string): string {
    const dateFormat = "MMM dd, yyyy 'at' h:mm a zzz";

    if (!timezone) {
      return format(date, dateFormat);
    }

    return formatInTimeZone(date, timezone, dateFormat);
  }

  @action
  trackIncidentSelected(entry: MessageLogEntry): void {
    if (!this.featureFlags.isEnabled('emno-end-incident-web') || entry.incidentStatus === null) return;
    this.metrics.trackEvent('WEB_INCIDENT_SELECTED', {
      company_id: this.state.currentCompany?.id,
      location_id: this.currentLocation.location?.id,
    });
  }

  protected trackCreateIncidentTapped(): void {
    if (this.featureFlags.isEnabled('emno-end-incident-web')) {
      this.metrics.trackEvent('WEB_CREATE_NEW_INCIDENT_BUTTON_TAPPED', {
        company_id: this.state.currentCompany?.id,
        location_id: this.currentLocation.location?.id,
      });
    }
  }

  private async fetchMissingAnnouncements(
    loadedIds: Set<string>,
    startDate: Date,
    endDate: Date,
  ): Promise<AnnouncementModel[]> {
    const missingIds = [...this.incidentsByAnnouncementId.entries()]
      .filter(([, incident]) => {
        const incidentStart = new Date(incident.startedAt);
        const incidentEnd = incident.endedAt ? new Date(incident.endedAt) : null;
        return incidentStart <= endDate && (incidentEnd === null || incidentEnd >= startDate);
      })
      .filter(([id]) => !loadedIds.has(id))
      .map(([id]) => id);
    return Promise.all(
      missingIds.map(
        (id) =>
          this.store.findRecord('announcement', id, {
            adapterOptions: { v3: true },
            include: 'creator-employee,takeovers',
            reload: true,
          }) as unknown as Promise<AnnouncementModel>,
      ),
    );
  }

  protected async createMessageLogEntry(announcement: AnnouncementModel): Promise<MessageLogEntry> {
    const creator = await announcement.creatorEmployee;
    const locationId = announcement.belongsTo('location').id();
    const locationName = this.locationName(locationId);
    const stats = announcement.statistics;
    // eslint-disable-next-line ember/use-ember-get-and-set
    const incidentCandidate = this.incidentsByAnnouncementId.get(announcement.id);
    const incidentStart = incidentCandidate ? new Date(incidentCandidate.startedAt) : null;
    const incidentEnd = incidentCandidate?.endedAt ? new Date(incidentCandidate.endedAt) : null;
    const incidentInRange =
      incidentStart !== null &&
      incidentStart <= this.endDateWithDefault &&
      (incidentEnd === null || incidentEnd >= this.startDateWithDefault);
    const incident = incidentInRange ? incidentCandidate : undefined;

    return {
      id: announcement.id,
      sentAt: this.formatDate(announcement.createdAt, this.state.currentLocation.timezone),
      sender: creator.name || 'Unknown',
      senderId: creator.id,
      senderProfilePictureUrl: creator.profilePictureUrl,
      messageTitle: announcement.title,
      messageBody: announcement.message,
      location: locationName || '',
      employeeCount: stats?.sent_employee_recipients ?? 0,
      visitorCount: stats?.visitor_recipients ?? 0,
      markedAsSafeCount: stats?.safe_recipients ?? 0,
      incidentStatus: incident?.status ?? null,
      activePeriod: incident
        ? this.formatActivePeriod(
            new Date(incident.startedAt),
            incident.endedAt ? new Date(incident.endedAt) : null,
            this.state.currentLocation.timezone,
          )
        : null,
    };
  }
}
